import { useEffect, useRef, useState } from 'react';
import { resolveFxMultiplier } from '../../../../commonSubEntities/accounting/frankfurterFx';
import { bpAccountingRow } from '../../../../../../models/entities/businessPartner/accounting';

export type CostInBaseState =
    | { status: 'no_base' }
    | { status: 'loading' }
    | { status: 'ok'; total: number }
    | { status: 'error' };

/** Converts each row’s `totalCost` from `currency` into the BP base currency. */
export function useCostInBase(rows: bpAccountingRow[], baseCurrency: string | null | undefined) {
    const [byShipment, setByShipment] = useState<Record<string, CostInBaseState>>({});
    const fxCacheRef = useRef<Map<string, number>>(new Map());

    useEffect(() => {
        let cancelled = false;
        const base = (baseCurrency ?? '').trim().toUpperCase();

        if (!base) {
            const initial: Record<string, CostInBaseState> = {};
            for (const r of rows) initial[r.shipmentId] = { status: 'no_base' };
            setByShipment(initial);
            return () => {
                cancelled = true;
            };
        }

        const initial: Record<string, CostInBaseState> = {};
        for (const r of rows) initial[r.shipmentId] = { status: 'loading' };
        setByShipment(initial);

        const run = async () => {
            const cache = fxCacheRef.current;

            await Promise.all(
                rows.map(async (row) => {
                    const date = row.shipmentDate ? new Date(row.shipmentDate) : new Date();
                    const from = (row.currency || base).trim().toUpperCase();
                    const amount = row.totalCost;

                    if (!Number.isFinite(amount)) {
                        if (!cancelled) {
                            setByShipment((prev) => ({
                                ...prev,
                                [row.shipmentId]: { status: 'ok', total: 0 }
                            }));
                        }
                        return;
                    }

                    if (from === base) {
                        if (!cancelled) {
                            setByShipment((prev) => ({
                                ...prev,
                                [row.shipmentId]: { status: 'ok', total: Math.round(amount * 100) / 100 }
                            }));
                        }
                        return;
                    }

                    const mult = await resolveFxMultiplier(from, base, date, cache);
                    if (cancelled) return;
                    if (mult == null) {
                        setByShipment((prev) => ({
                            ...prev,
                            [row.shipmentId]: { status: 'error' }
                        }));
                        return;
                    }
                    const total = Math.round(amount * mult * 100) / 100;
                    setByShipment((prev) => ({
                        ...prev,
                        [row.shipmentId]: { status: 'ok', total }
                    }));
                })
            );
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, [rows, baseCurrency]);

    return byShipment;
}
